import { useEffect, useState } from 'react';
import { verifyToken } from '../lib/jwtUtils';

const prefix = 'bayesprep:profile-name:';
const lifetime = 24 * 60 * 60 * 1000;
type Draft = { name: string; original: string; expires: number };
const memory = new Map<string, Draft>();

export function clearProfileNameDrafts() {
  memory.clear();
  try { Object.keys(sessionStorage).filter(key => key.startsWith(prefix)).forEach(key => sessionStorage.removeItem(key)); } catch { /* Memory still clears. */ }
}

/** Name only, scoped to actor + institution; credentials are never stored here. */
export function useProfileNameDraft(user: { id: string; name?: string } | null) {
  const original = user?.name || '';
  let key = '';
  try {
    const identity = verifyToken(localStorage.getItem('token') || '');
    if (user?.id && typeof identity?.tenantId === 'string' && identity.tenantId.trim()
      && typeof identity.sub === 'string' && identity.sub.trim()) {
      key = prefix + JSON.stringify([identity.tenantId, identity.sub, user.id]);
    }
  } catch { /* Never invent recovery identity. */ }
  const [name, setName] = useState(original);
  const [isEditing, setIsEditing] = useState(false);
  const [durable, setDurable] = useState(true);
  useEffect(() => {
    let draft: Draft | undefined;
    let available = Boolean(key);
    if (key) {
      try { const raw = sessionStorage.getItem(key); draft = raw ? JSON.parse(raw) : memory.get(key); if (!raw && draft) available = false; }
      catch { draft = memory.get(key); available = false; }
    }
    const valid = draft && typeof draft.name === 'string' && draft.name.length <= 100
      && draft.original === original && Number.isFinite(draft.expires) && draft.expires > Date.now();
    setDurable(available);
    setName(valid && draft ? draft.name : original);
    setIsEditing(Boolean(valid));
    if (!valid && key) { memory.delete(key); try { sessionStorage.removeItem(key); } catch { /* No write required. */ } }
  }, [key, original]);
  const dirty = isEditing && name !== original;
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { if (!durable) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, durable]);
  const changeName = (value: string) => {
    setName(value);
    if (!key) { setDurable(false); return; }
    const draft = { name: value, original, expires: Date.now() + lifetime };
    memory.set(key, draft);
    try { sessionStorage.setItem(key, JSON.stringify(draft)); setDurable(true); }
    catch { setDurable(false); }
  };
  const clearDraft = () => {
    if (key) { memory.delete(key); try { sessionStorage.removeItem(key); } catch { /* Memory copy clears independently. */ } }
    setName(original); setIsEditing(false); setDurable(true);
  };
  return { name, changeName, isEditing, setIsEditing, clearDraft, dirty, durable };
}
